'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BonusShopApi, BonusShopFs } from '@/api/cms/bonusShop';
import { CurrencyCode, getCurrencyName } from '@/helpers/currency';
import { timestamp } from '@/helpers/dateTime';
import type { Locale } from '@/helpers/languages';
import { MAP_LOCALE_TO_CURRENCY } from '@/modules/shared';
import { useGetCommonStore } from '@/store';
import { normalizeProducts } from '../normalize';

const MAX_TIMEOUT_MS = 2_147_483_647;

interface BonusShopProductsParams {
  shop: BonusShopApi;
  freeSpins: BonusShopFs[];
}

export const useBonusShopProducts = ({
  shop,
  freeSpins,
}: BonusShopProductsParams) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('bonusShop');
  const [now, setNow] = useState(() => Date.now());
  const useCommonStore = useGetCommonStore();
  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const storeWallet = useCommonStore.use.currentWallet();
  const currentWallet = isAuthenticated ? storeWallet : null;
  const localeCurrency = MAP_LOCALE_TO_CURRENCY[language as Locale];

  const walletCurrency = useMemo(
    () => ({
      currency: currentWallet
        ? getCurrencyName(currentWallet.currency)
        : localeCurrency || CurrencyCode.EUR,
      isCrypto: !!currentWallet?.isCrypto,
      isGuest: !currentWallet,
    }),
    [currentWallet, localeCurrency]
  );

  const products = useMemo(
    () => normalizeProducts(shop, freeSpins, walletCurrency, t),
    [shop, freeSpins, walletCurrency, t]
  );

  const visibleProducts = useMemo(
    () =>
      products.filter(
        ({ durationTo }) => !durationTo || timestamp(durationTo) > now
      ),
    [products, now]
  );

  useEffect(() => {
    const msLeft = visibleProducts
      .filter(({ durationTo }) => !!durationTo)
      .map(({ durationTo }) => timestamp(durationTo as string) - Date.now())
      .filter((ms) => ms > 0 && ms < MAX_TIMEOUT_MS);

    if (!msLeft.length) return;

    const timer = setTimeout(() => setNow(Date.now()), Math.min(...msLeft));

    return () => clearTimeout(timer);
  }, [visibleProducts]);

  return { products: visibleProducts };
};
